import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DollarSign, TrendingUp, Wallet, CalendarClock, PieChart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Iter 58 — Property Service Revenue & Lifetime Value card.
 *
 * Financial permissions are inherited from the backend: `require_role(admin+)`.
 * We ALSO hide the card up-front for technician/sales so those roles never
 * even issue the request. Any future permission change on the backend flows
 * through automatically — the frontend's role-gating is a defense in depth.
 */
const CAN_VIEW_REVENUE_ROLES = new Set(["admin", "owner"]);

const fmtUsd = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 2,
  });
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch { return "—"; }
};

const CategoryChip = ({ cat }) => (
  <div
    className="flex items-center justify-between border border-zinc-200 rounded-lg px-3 py-2 bg-white"
    data-testid={`pv-category-${cat.category}`}
  >
    <div className="flex flex-col">
      <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono">
        {cat.category}
      </div>
      <div className="text-[11px] text-zinc-400">
        {cat.line_count} line{cat.line_count === 1 ? "" : "s"}
      </div>
    </div>
    <div className="text-right">
      <div className="text-sm font-semibold text-zinc-900" data-testid={`pv-cat-invoiced-${cat.category}`}>
        {fmtUsd(cat.invoiced)}
      </div>
      <div className="text-[11px] text-emerald-700">paid {fmtUsd(cat.paid)}</div>
    </div>
  </div>
);

const KpiTile = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div
    className="bg-white border border-zinc-200 rounded-lg p-3 flex items-start gap-3"
    data-testid={testid}
  >
    <div className={`shrink-0 w-9 h-9 rounded-md flex items-center justify-center ${accent}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className="text-lg font-semibold text-zinc-900 mt-0.5 truncate">{value}</div>
      {sub ? <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div> : null}
    </div>
  </div>
);

export default function PropertyValueSummary({ propertyId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState(null);

  const canView = user && CAN_VIEW_REVENUE_ROLES.has(user.role);

  useEffect(() => {
    if (!propertyId || !canView) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get(`/properties/${propertyId}/value-summary`);
        if (mounted) setData(r.data);
      } catch (e) {
        if (!mounted) return;
        if (e?.response?.status === 403) setForbidden(true);
        else setError("Unable to load property value summary.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [propertyId, canView]);

  if (!propertyId) return null;
  if (!canView || forbidden) return null;
  if (loading && !data) {
    return (
      <div
        className="border border-zinc-200 rounded-xl bg-zinc-50 p-6 text-sm text-zinc-500"
        data-testid="pv-loading"
      >Loading property value…</div>
    );
  }
  if (error) {
    return (
      <div
        className="border border-rose-200 rounded-xl bg-rose-50 p-4 text-sm text-rose-700"
        data-testid="pv-error"
      >{error}</div>
    );
  }
  if (!data) return null;

  const revenue = data.revenue || {};
  const jobs = data.jobs || {};
  const estimates = data.estimates || {};
  const categories = data.service_categories || [];
  const hasAnyActivity =
    (revenue.invoiced || 0) > 0 ||
    (jobs.total_count || 0) > 0 ||
    (estimates.total_estimates || 0) > 0;

  return (
    <div
      className="border border-zinc-200 rounded-xl bg-white p-5"
      data-testid="pv-summary-card"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#FF5A00]" />
            <h3 className="text-base font-semibold text-zinc-900">
              Property revenue & lifetime value
            </h3>
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">
            Scoped to this property only · USD
          </div>
        </div>
      </div>

      {!hasAnyActivity ? (
        <div
          className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg p-4"
          data-testid="pv-empty-state"
        >
          No revenue, jobs, or estimates recorded on this property yet. Once
          this property has a completed job or invoice, its lifetime value
          will appear here.
        </div>
      ) : (
        <>
          {/* Revenue KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <KpiTile
              icon={DollarSign}
              label="Invoiced"
              value={fmtUsd(revenue.invoiced)}
              accent="bg-orange-50 text-[#FF5A00]"
              testid="pv-kpi-invoiced"
            />
            <KpiTile
              icon={Wallet}
              label="Paid"
              value={fmtUsd(revenue.paid)}
              accent="bg-emerald-50 text-emerald-700"
              testid="pv-kpi-paid"
            />
            <KpiTile
              icon={DollarSign}
              label="Outstanding"
              value={fmtUsd(revenue.outstanding)}
              accent="bg-rose-50 text-rose-700"
              testid="pv-kpi-outstanding"
            />
          </div>

          {/* Jobs + Estimates KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            <KpiTile
              icon={CalendarClock}
              label="Completed jobs"
              value={jobs.completed_count ?? 0}
              sub={`Avg ${fmtUsd(jobs.avg_completed_value)}`}
              accent="bg-zinc-100 text-zinc-700"
              testid="pv-kpi-completed-jobs"
            />
            <KpiTile
              icon={CalendarClock}
              label="First service"
              value={fmtDate(jobs.first_service_at)}
              accent="bg-zinc-100 text-zinc-700"
              testid="pv-kpi-first-service"
            />
            <KpiTile
              icon={CalendarClock}
              label="Recent service"
              value={fmtDate(jobs.last_service_at)}
              accent="bg-zinc-100 text-zinc-700"
              testid="pv-kpi-recent-service"
            />
            <KpiTile
              icon={TrendingUp}
              label="Estimate win rate"
              value={`${estimates.estimate_conversion_rate ?? 0}%`}
              sub={`${estimates.accepted_estimates ?? 0} / ${estimates.total_estimates ?? 0} accepted`}
              accent="bg-blue-50 text-blue-700"
              testid="pv-kpi-conversion"
            />
          </div>

          {/* Categories */}
          {categories.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <PieChart className="w-3.5 h-3.5 text-zinc-500" />
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">
                  Revenue by service category
                </div>
              </div>
              <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                data-testid="pv-categories-list"
              >
                {categories.map((c) => <CategoryChip key={c.category} cat={c} />)}
              </div>
            </div>
          )}

          {/* Profitability placeholder — clearly labeled */}
          <div
            className="mt-4 border border-dashed border-zinc-200 rounded-lg p-3 flex items-center justify-between bg-zinc-50/60"
            data-testid="pv-profitability-placeholder"
          >
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Profitability</div>
              <div className="text-xs text-zinc-500 mt-0.5">
                Job costing data isn't available yet. Once costs are tracked
                per job, estimated profit and margin will appear here.
              </div>
            </div>
            <div className="text-xs text-zinc-400 italic">coming soon</div>
          </div>
        </>
      )}
    </div>
  );
}
